import {
  type FieldSkipReason,
  type TopicFieldRow,
} from "@/shared/domain/sessions";
import {
  CanonicalDataType,
  type SchemaFieldRecord,
} from "@/shared/domain/topics";
import { Table } from "@/shared/foundation/primitives";
import { pluralize } from "@/shared/text";

import styles from "./TopicFieldStatsTable.module.css";

type FieldStats = Partial<Record<string, number>>;

/** The stat columns, in order: numbers fill the first five, booleans the last two. */
const STAT_COLUMNS = [
  { stat: "min", label: "Min" },
  { stat: "max", label: "Max" },
  { stat: "mean", label: "Mean" },
  { stat: "median", label: "Median" },
  { stat: "stddev", label: "Std dev" },
  { stat: "true_count", label: "True" },
  { stat: "false_count", label: "False" },
] as const;

const COUNT_STATS: ReadonlySet<string> = new Set(["true_count", "false_count"]);

const countFormat = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 0,
});
const valueFormat = new Intl.NumberFormat(undefined, {
  maximumSignificantDigits: 6,
});

function statText(stats: FieldStats, stat: string): string {
  const value = stats[stat];
  if (value === undefined) {
    return "—";
  }
  return COUNT_STATS.has(stat)
    ? countFormat.format(value)
    : valueFormat.format(value);
}

function skipReasonText(
  reason: FieldSkipReason,
  canonicalType: string,
): string {
  switch (reason) {
    case "array":
      return "No stats for array fields";
    case "inside_array":
      return "No stats for fields inside arrays";
    case "count_only_topic":
      return "No stats for fields of image or point-cloud topics";
    case "unsupported_type":
      return `No stats for ${canonicalType} fields`;
  }
}

const pathKey = (path: readonly string[]) => JSON.stringify(path);

/** How many fields sit directly under each object field, by path. */
function childCounts(rows: readonly TopicFieldRow[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const { field } of rows) {
    const path = field.path_in_schema;
    if (path.length > 1) {
      const parent = pathKey(path.slice(0, -1));
      counts.set(parent, (counts.get(parent) ?? 0) + 1);
    }
  }
  return counts;
}

/**
 * What a field's stat cells show in place of stats: how many fields an object
 * holds, why the field gets no stats, or that none were published for it.
 * `null` when the field has stats.
 */
function noStatsText(
  row: TopicFieldRow,
  children: number | undefined,
): string | null {
  const { field, plan, stats } = row;
  if (
    field.canonical_data_type === CanonicalDataType.Object &&
    children !== undefined &&
    plan.skipReason !== "inside_array"
  ) {
    return `${children.toLocaleString()} ${pluralize("field", children)}`;
  }
  if (plan.skipReason !== null) {
    return skipReasonText(plan.skipReason, field.canonical_data_type);
  }
  return stats === null ? "No stats published for this field" : null;
}

export interface TopicFieldStatsTableProps {
  rows: readonly TopicFieldRow[];
  /** Whether the stat columns are shown; without them the table lists the fields and their types. */
  statsShown: boolean;
}

/**
 * The fields of one topic schema, each with its types and the stats the
 * session's data gives it in fixed columns: min, max, mean, median and
 * standard deviation for a number, true and false counts for a boolean. An
 * object field says how many fields it holds; any other field without stats
 * says why.
 */
export function TopicFieldStatsTable({
  rows,
  statsShown,
}: TopicFieldStatsTableProps) {
  const children = childCounts(rows);
  return (
    <div className={styles.scroll}>
      <Table aria-label="Fields" layout="fixed">
        {statsShown && (
          <colgroup>
            <col className={styles.fieldColumn} />
            {STAT_COLUMNS.map(({ stat }) => (
              <col key={stat} />
            ))}
          </colgroup>
        )}
        <Table.Header variant="compact">
          <Table.Row>
            <Table.Head>Field</Table.Head>
            {statsShown &&
              STAT_COLUMNS.map(({ stat, label }) => (
                <Table.Head key={stat} className={styles.number}>
                  {label}
                </Table.Head>
              ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map((row) => (
            <Table.Row key={row.field.field_id}>
              <FieldCell field={row.field} />
              {statsShown && (
                <StatCells
                  row={row}
                  noStats={noStatsText(
                    row,
                    children.get(pathKey(row.field.path_in_schema)),
                  )}
                />
              )}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </div>
  );
}

/** A field's name, indented by its depth, over its types. */
function FieldCell({ field }: { field: SchemaFieldRecord }) {
  const path = field.path_in_schema;
  return (
    <Table.Head scope="row" className={styles.fieldCell}>
      <span
        className={styles.field}
        style={{
          paddingLeft: `calc(${path.length - 1} * var(--spacing-md))`,
        }}
      >
        <span className={styles.path} title={path.join(".")}>
          {path[path.length - 1]}
        </span>
        <span className={styles.types}>
          {field.data_type} · {field.canonical_data_type}
        </span>
      </span>
    </Table.Head>
  );
}

function StatCells({
  row,
  noStats,
}: {
  row: TopicFieldRow;
  noStats: string | null;
}) {
  if (noStats !== null || row.stats === null) {
    return (
      <Table.Cell colSpan={STAT_COLUMNS.length} className={styles.noStats}>
        {noStats}
      </Table.Cell>
    );
  }
  const stats = row.stats;
  return (
    <>
      {STAT_COLUMNS.map(({ stat }) => (
        <Table.Cell key={stat} className={styles.number}>
          {statText(stats, stat)}
        </Table.Cell>
      ))}
    </>
  );
}
